import { FC, useMemo } from "react"
import {
  Box,
  useTheme,
  Paper,
  Avatar,
  Typography,
  Stack,
  Divider,
  Button,
} from "@mui/material"
import clsx from "clsx"
import { deepmerge } from "@mui/utils"

import { IFeatureDetailResult } from "hooks/feature/useFeatureDetail"
import { IOccupantDetail, IPostDetail, ISectionDetail } from "utils/feature"

import { InfoItem } from "extensions/one-siam/components/InfoItem"
import { LayerIcon } from "extensions/one-siam/components/icons"
import { SkeletonNextopiaPlaceDetailCard } from "extensions/one-siam/components/domains/features"

import { QRCode } from "components/QRCode"
import { useTranslation } from "providers/i18n"
import { compact, get } from "lodash"
import { NEXTOPIA_CATEGORY_TYPE } from "extensions/one-siam/constants"
import { PostMediaCard } from "../../../post"

export interface NextopiaPlaceDetailCardProps {
  data: IFeatureDetailResult & IOccupantDetail & ISectionDetail
  highlights?: IPostDetail
  mobileUrl?: string
  onNavigate: () => void
  onViewAllShops: () => void
  onHighlightClick: (data: IPostDetail) => void
  canNavigate?: boolean
  loaded?: boolean
}
export const NextopiaPlaceDetailCard: FC<NextopiaPlaceDetailCardProps> = ({
  data,
  mobileUrl = null,
  onNavigate,
  onViewAllShops,
  onHighlightClick,
  canNavigate = false,
  loaded = false,
  highlights = [],
}) => {
  const {
    parentCategoryRefs,
    description,
    featuredImage,
    coverImage,
    feature,
    correlation,
  } = data || {}

  const viewAllNextopiaShopsPath = useMemo(() => {
    const parentCategories = get(
      feature,
      "properties.local_parent_categories",
      []
    )
    const venueId = get(feature, "properties.venue_id", [])
    const matchedCategory = parentCategories.find(
      (category) =>
        get(category, "properties.reference") === NEXTOPIA_CATEGORY_TYPE
    )
    return matchedCategory?.id
      ? `/directory?parentCategoryId=${matchedCategory.id}&subCategoryId=ALL&ordinal=ALL&venue=${venueId}`
      : null
  }, [feature])

  const floorsText = useMemo(() => {
    const floors = compact(
      correlation?.floorOptions.map((option) => option.levelName)
    )
    return floors.length > 0 ? floors.join(", ") : "-"
  }, [correlation])

  const { t } = useTranslation()
  const theme = useTheme()
  const {
    container,
    primarySection,
    secondarySection,
    innerSection,
    headerContainer,
    descriptionContainer,
    avatarContainer,
    nextopiaLogo,
    detailContainer,
    listInfoContainer,
    listInfoDivider,
    listInfoItem,
    divider,
    descriptionText,
    qrCode,
    qrCodeContainer,
    qrCodeHelperText,
    actionContainer,
    actionButton,
    highlightSection,
    highlightTitleContainer,
    highlightTitleText,
    highlightList,
    highlightCard,
  } = theme.components[`NextopiaPlaceDetailCard`]?.styleOverrides || {}
  const hasHighlights = Array.isArray(highlights) && highlights.length > 0

  if (!loaded) return <SkeletonNextopiaPlaceDetailCard />

  return (
    <Paper sx={container} className={clsx(parentCategoryRefs)}>
      <Box
        sx={deepmerge(primarySection, {
          backgroundImage: `linear-gradient(270deg, rgba(255, 255, 255, 0.00) 8.18%, #FFF 65.7%), url(${coverImage})`,
        })}
      >
        <Box sx={innerSection}>
          <Box sx={headerContainer}>
            <Box sx={avatarContainer}>
              <Avatar sx={nextopiaLogo} src={featuredImage} />
            </Box>
            <Box sx={detailContainer}>
              <Stack
                sx={listInfoContainer}
                direction="row"
                spacing={2}
                divider={
                  <Divider
                    sx={listInfoDivider}
                    orientation="vertical"
                    flexItem
                  />
                }
              >
                <InfoItem
                  className="listInfoItem--zone"
                  sx={listInfoItem}
                  label={t("Zone")}
                />
                <InfoItem
                  className="listInfoItem--level"
                  sx={listInfoItem}
                  icon={<LayerIcon />}
                  label={t(floorsText)}
                />
              </Stack>
            </Box>
          </Box>
          <Divider sx={divider} />
          <Box sx={descriptionContainer}>
            <Typography variant="body1" sx={descriptionText}>
              {t(description)}
            </Typography>
          </Box>
          {hasHighlights && (
            <Box sx={highlightSection}>
              <Box sx={highlightTitleContainer}>
                <Typography variant="h6" sx={highlightTitleText}>
                  {t("Highlight")}
                </Typography>
              </Box>
              <Box sx={highlightList}>
                {highlights.map((data) => (
                  <PostMediaCard
                    sx={highlightCard}
                    key={`highlight-nextopia-${data?.id}`}
                    data={data}
                    onClick={onHighlightClick}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={secondarySection}>
        {mobileUrl && (
          <Box sx={qrCodeContainer}>
            <QRCode sx={qrCode} url={mobileUrl} />
            <Stack>
              <Typography sx={qrCodeHelperText}>
                {t("Scan for direction")}
              </Typography>
              <Typography sx={qrCodeHelperText}>{t("On mobile")}</Typography>
            </Stack>
          </Box>
        )}
        <Box sx={actionContainer}>
          <Button
            variant="contained"
            onClick={onViewAllShops}
            color="secondary"
            sx={{
              ...actionButton,
              visibility: viewAllNextopiaShopsPath ? "visible" : "hidden",
            }}
          >
            {t("View all shops")}
          </Button>
          <Button
            variant="contained"
            onClick={onNavigate}
            color="primary"
            sx={{
              ...actionButton,
              visibility: canNavigate ? "visible" : "hidden",
            }}
          >
            {t("Get direction")}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
